import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Container,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  InputAdornment,
  IconButton,
  Grid,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Chip,
  Avatar
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  Email,
  Lock,
  Person,
  Phone,
  LocationOn,
  Home,
  Business,
  Security,
  CheckCircle,
  Error
} from '@mui/icons-material';
import MainService from '../services/MainService';

const Register = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    emailId: '',
    mobileNo: '',
    address: '',
    location: '',
    city: '',
    poBox: '',
    type: '',
    isWithAddress: true,
    password: '',
    confirmPassword: ''
  });
  
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState({
    score: 0,
    feedback: ''
  });

  const validatePassword = (password) => {
    let score = 0;
    let feedback = [];

    if (password.length >= 8) {
      score += 1;
    } else {
      feedback.push('At least 8 characters');
    }

    if (/[a-z]/.test(password)) {
      score += 1;
    } else {
      feedback.push('At least one lowercase letter');
    }

    if (/[A-Z]/.test(password)) {
      score += 1;
    } else {
      feedback.push('At least one uppercase letter');
    }

    if (/[0-9]/.test(password)) {
      score += 1;
    } else {
      feedback.push('At least one number');
    }

    if (/[^A-Za-z0-9]/.test(password)) {
      score += 1;
    } else {
      feedback.push('At least one special character');
    }

    return {
      score,
      feedback: feedback.join(', ')
    };
  };

  const handleChange = (e) => {
    const { name, value, checked } = e.target;
    setFormData({
      ...formData,
      [name]: name === 'isWithAddress' ? checked : value
    });

    if (name === 'password') {
      setPasswordStrength(validatePassword(value));
    }
  };

  const validateForm = () => {
    const errors = [];

    if (!formData.name.trim()) {
      errors.push('Name is required');
    }

    if (!formData.emailId.trim()) {
      errors.push('Email is required');
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.emailId)) {
      errors.push('Please enter a valid email address');
    }

    if (!formData.mobileNo.trim()) {
      errors.push('Mobile number is required');
    } else if (!/^[0-9]{10,15}$/.test(formData.mobileNo.replace(/\s/g, ''))) {
      errors.push('Please enter a valid mobile number');
    }

    if (!formData.location.trim()) {
      errors.push('Location is required');
    }

    if (!formData.city.trim()) {
      errors.push('City is required');
    }

    if (!formData.address.trim()) {
      errors.push('Address is required');
    }

    if (!formData.type.trim()) {
      errors.push('Type is required');
    }

    if (!formData.password) {
      errors.push('Password is required');
    } else if (passwordStrength.score < 3) {
      errors.push('Password is too weak. Please follow the requirements.');
    }

    if (!formData.confirmPassword) {
      errors.push('Confirm password is required');
    } else if (formData.password !== formData.confirmPassword) {
      errors.push('Passwords do not match');
    }

    return errors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const validationErrors = validateForm();
    if (validationErrors.length > 0) {
      setError(validationErrors.join(', '));
      setLoading(false);
      return;
    }

    try {
      const response = await MainService.registerCustomer({
        name: formData.name,
        emailId: formData.emailId,
        mobileNo: formData.mobileNo,
        address: formData.address,
        location: formData.location,
        city: formData.city,
        poBox: formData.poBox,
        type: formData.type,
        isWithAddress: formData.isWithAddress,
        password: formData.password
      });

      if (response === 1) {
        setError('');
        alert('Registration successful, Please login to continue');
        navigate('/login');
      } else if (response === 2) {
        setError('User already exists with this email');
      } else if (response === 3) {
        setError('Mobile number is already registered');
      } else {
        setError('Registration failed. Please try again.');
      }
    } catch (error) {
      setError('Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getPasswordStrengthColor = () => {
    if (passwordStrength.score <= 1) return '#f44336';
    if (passwordStrength.score <= 2) return '#ff9800';
    if (passwordStrength.score <= 3) return '#ffeb3b';
    return '#4caf50';
  };

  const getPasswordStrengthText = () => {
    if (passwordStrength.score <= 1) return 'Very Weak';
    if (passwordStrength.score <= 2) return 'Weak';
    if (passwordStrength.score <= 3) return 'Fair';
    if (passwordStrength.score <= 4) return 'Good';
    return 'Strong';
  };

     return (
     <Box
       sx={{
         minHeight: '100vh',
         width: '100%',
         background: `linear-gradient(rgba(0, 0, 0, 0.4), rgba(0, 0, 0, 0.4)), url('https://images.unsplash.com/photo-1500382017468-9049fed747ef?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80')`,
         backgroundSize: 'cover',
         backgroundPosition: 'center',
         backgroundRepeat: 'no-repeat',
         display: 'flex',
         alignItems: 'center',
         justifyContent: 'center',
         m: 0,
         p: 0,
         overflow: 'auto',
         overflowX: 'hidden'
       }}
     >
       <Container maxWidth="md" sx={{ m: 0, p: 2, width: '100%', maxWidth: '100%', overflowX: 'hidden' }}>
        <Grid container spacing={4} justifyContent="center">
          {/* Left Side - Branding */}
          <Grid item xs={12} md={5}>
            <Box sx={{ 
              textAlign: 'center', 
              color: 'white',
              display: { xs: 'none', md: 'block' }
            }}>
              <Box sx={{ mb: 4 }}>
                <img 
                  src="/logo.png" 
                  alt="UVANDUS Logo" 
                  style={{ 
                    height: '80px', 
                    width: 'auto',
                    maxWidth: '300px',
                    filter: 'brightness(0) invert(1)'
                  }} 
                />
              </Box>
              <Typography variant="h5" sx={{ mb: 3, opacity: 0.9 }}>
                Fresh Delivery Portal
              </Typography>
              <Typography variant="body1" sx={{ opacity: 0.8, lineHeight: 1.6 }}>
                Join thousands of customers who trust UVANDUS for their fresh delivery needs. 
                Create your account today and experience the difference.
              </Typography>
              
              <Box sx={{ mt: 4, display: 'flex', justifyContent: 'center', gap: 2 }}>
                <Chip 
                  icon={<CheckCircle />} 
                  label="Fast Delivery" 
                  sx={{ 
                    backgroundColor: 'rgba(255,255,255,0.2)', 
                    color: 'white',
                    '& .MuiChip-icon': { color: 'white' }
                  }} 
                />
                <Chip 
                  icon={<Security />} 
                  label="Secure" 
                  sx={{ 
                    backgroundColor: 'rgba(255,255,255,0.2)', 
                    color: 'white',
                    '& .MuiChip-icon': { color: 'white' }
                  }} 
                />
              </Box>
            </Box>
          </Grid>

          {/* Right Side - Registration Form */}
          <Grid item xs={12} md={7}>
            <Card 
              elevation={8}
              sx={{ 
                borderRadius: 4,
                background: 'rgba(255, 255, 255, 0.95)',
                backdropFilter: 'blur(20px)',
                border: '1px solid rgba(255, 255, 255, 0.3)'
              }}
            >
              <CardContent sx={{ p: 4 }}>
                                 {/* Mobile Header */}
                 <Box sx={{ 
                   textAlign: 'center', 
                   mb: 4,
                   display: { xs: 'block', md: 'none' }
                 }}>
                   <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#2E7D32', mb: 1 }}>
                     UVANDUS
                   </Typography>
                   <Typography variant="h6" sx={{ color: '#4CAF50', mb: 3 }}>
                     Create Account
                   </Typography>
                 </Box>

                 {/* Desktop Header */}
                 <Box sx={{ 
                   textAlign: 'center', 
                   mb: 4,
                   display: { xs: 'none', md: 'block' }
                 }}>
                   <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#2E7D32', mb: 1 }}>
                     Create Account
                   </Typography>
                   <Typography variant="body1" sx={{ color: '#666' }}>
                     Join our community today
                   </Typography>
                 </Box>

                {error && (
                  <Alert 
                    severity="error" 
                    sx={{ mb: 3 }}
                    icon={<Error />}
                  >
                    {error}
                  </Alert>
                )}

                <Box component="form" onSubmit={handleSubmit}>
                  <Grid container spacing={3}>
                                         {/* Personal Information */}
                     <Grid item xs={12}>
                       <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                         <Avatar sx={{ bgcolor: '#2E7D32', mr: 2 }}>
                           <Person />
                         </Avatar>
                         <Typography variant="h6" sx={{ color: '#2E7D32', fontWeight: 600 }}>
                           Personal Information
                         </Typography>
                       </Box>
                     </Grid>
                    
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Full Name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        variant="outlined"
                        InputProps={{
                                                     startAdornment: (
                             <InputAdornment position="start">
                               <Person sx={{ color: '#2E7D32' }} />
                             </InputAdornment>
                           ),
                        }}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            '& fieldset': {
                              borderColor: '#e0e0e0',
                            },
                            '&:hover fieldset': {
                              borderColor: '#2E7D32',
                            },
                            '&.Mui-focused fieldset': {
                              borderColor: '#2E7D32',
                            },
                          },
                          '& .MuiInputLabel-root': {
                            color: '#666',
                            '&.Mui-focused': {
                              color: '#2E7D32',
                            },
                          },
                          '& .MuiInputBase-input': {
                            color: '#333',
                          },
                        }}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Email"
                        name="emailId"
                        type="email"
                        value={formData.emailId}
                        onChange={handleChange}
                        variant="outlined"
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Email sx={{ color: '#2E7D32' }} />
                            </InputAdornment>
                          ),
                        }}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            '& fieldset': {
                              borderColor: '#e0e0e0',
                            },
                            '&:hover fieldset': {
                              borderColor: '#667eea',
                            },
                            '&.Mui-focused fieldset': {
                              borderColor: '#667eea',
                            },
                          },
                          '& .MuiInputLabel-root': {
                            color: '#666',
                            '&.Mui-focused': {
                              color: '#667eea',
                            },
                          },
                          '& .MuiInputBase-input': {
                            color: '#333',
                          },
                        }}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Mobile Number"
                        name="mobileNo"
                        value={formData.mobileNo}
                        onChange={handleChange}
                        variant="outlined"
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Phone sx={{ color: '#2E7D32' }} />
                            </InputAdornment>
                          ),
                        }}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            '& fieldset': {
                              borderColor: '#e0e0e0',
                            },
                            '&:hover fieldset': {
                              borderColor: '#2E7D32',
                            },
                            '&.Mui-focused fieldset': {
                              borderColor: '#2E7D32',
                            },
                          },
                          '& .MuiInputLabel-root': {
                            color: '#666',
                            '&.Mui-focused': {
                              color: '#2E7D32',
                            },
                          },
                          '& .MuiInputBase-input': {
                            color: '#333',
                          },
                        }}
                      />
                    </Grid>

                    {/* Address Information */}
                    <Grid item xs={12}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, mt: 2 }}>
                        <Avatar sx={{ bgcolor: '#4CAF50', mr: 2 }}>
                          <LocationOn />
                        </Avatar>
                        <Typography variant="h6" sx={{ color: '#4CAF50', fontWeight: 600 }}>
                          Address Information
                        </Typography>
                      </Box>
                    </Grid>

                                         <Grid item xs={12} sm={6}>
                       <TextField
                         fullWidth
                         label="Location"
                         name="location"
                         value={formData.location}
                         onChange={handleChange}
                         variant="outlined"
                         InputProps={{
                           startAdornment: (
                             <InputAdornment position="start">
                               <LocationOn sx={{ color: '#4CAF50' }} />
                             </InputAdornment>
                           ),
                         }}
                                                 sx={{
                           '& .MuiOutlinedInput-root': {
                             '& fieldset': {
                               borderColor: '#e0e0e0',
                             },
                             '&:hover fieldset': {
                               borderColor: '#4CAF50',
                             },
                             '&.Mui-focused fieldset': {
                               borderColor: '#4CAF50',
                             },
                           },
                           '& .MuiInputLabel-root': {
                             color: '#666',
                             '&.Mui-focused': {
                               color: '#4CAF50',
                             },
                           },
                           '& .MuiInputBase-input': {
                             color: '#333',
                           },
                         }}
                       />
                     </Grid>

                     <Grid item xs={12} sm={6}>
                       <TextField
                         fullWidth
                         label="City"
                         name="city"
                         value={formData.city}
                         onChange={handleChange}
                         variant="outlined"
                         InputProps={{
                           startAdornment: (
                             <InputAdornment position="start">
                               <LocationOn sx={{ color: '#4CAF50' }} />
                             </InputAdornment>
                           ),
                         }}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            '& fieldset': {
                              borderColor: '#e0e0e0',
                            },
                            '&:hover fieldset': {
                              borderColor: '#4CAF50',
                            },
                            '&.Mui-focused fieldset': {
                              borderColor: '#4CAF50',
                            },
                          },
                          '& .MuiInputLabel-root': {
                            color: '#666',
                            '&.Mui-focused': {
                              color: '#4CAF50',
                            },
                          },
                          '& .MuiInputBase-input': {
                            color: '#333',
                          },
                        }}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Address"
                        name="address"
                        multiline
                        rows={3}
                        value={formData.address}
                        onChange={handleChange}
                        variant="outlined"
                        InputProps={{
                                                     startAdornment: (
                             <InputAdornment position="start">
                               <Home sx={{ color: '#4CAF50' }} />
                             </InputAdornment>
                           ),
                        }}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            '& fieldset': {
                              borderColor: '#e0e0e0',
                            },
                            '&:hover fieldset': {
                              borderColor: '#4CAF50',
                            },
                            '&.Mui-focused fieldset': {
                              borderColor: '#4CAF50',
                            },
                          },
                          '& .MuiInputLabel-root': {
                            color: '#666',
                            '&.Mui-focused': {
                              color: '#4CAF50',
                            },
                          },
                          '& .MuiInputBase-input': {
                            color: '#333',
                          },
                        }}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="PO Box"
                        name="poBox"
                        value={formData.poBox}
                        onChange={handleChange}
                        variant="outlined"
                        InputProps={{
                                                     startAdornment: (
                             <InputAdornment position="start">
                               <Business sx={{ color: '#4CAF50' }} />
                             </InputAdornment>
                           ),
                        }}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            '& fieldset': {
                              borderColor: '#e0e0e0',
                            },
                            '&:hover fieldset': {
                              borderColor: '#764ba2',
                            },
                            '&.Mui-focused fieldset': {
                              borderColor: '#764ba2',
                            },
                          },
                          '& .MuiInputLabel-root': {
                            color: '#666',
                            '&.Mui-focused': {
                              color: '#764ba2',
                            },
                          },
                          '& .MuiInputBase-input': {
                            color: '#333',
                          },
                        }}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth>
                        <Select
                          name="type"
                          value={formData.type}
                          onChange={handleChange}
                          label=""
                          displayEmpty
                                                     sx={{
                             '& .MuiOutlinedInput-root': {
                               '& fieldset': {
                                 borderColor: '#e0e0e0',
                               },
                               '&:hover fieldset': {
                                 borderColor: '#4CAF50',
                               },
                               '&.Mui-focused fieldset': {
                                 borderColor: '#4CAF50',
                               },
                             },
                             '& .MuiInputLabel-root': {
                               color: '#666',
                               '&.Mui-focused': {
                                 color: '#4CAF50',
                               },
                             },
                             '& .MuiSelect-select': {
                               color: formData.type ? '#333' : '#999',
                             },
                           }}
                        >
                          <MenuItem value="">
                            <em>Address Type</em>
                          </MenuItem>
                          <MenuItem value="individual">Individual</MenuItem>
                          <MenuItem value="business">Business</MenuItem>
                          <MenuItem value="corporate">Corporate</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>

                    <Grid item xs={12}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={formData.isWithAddress}
                            onChange={handleChange}
                            name="isWithAddress"
                                                       sx={{
                             color: '#4CAF50',
                             '&.Mui-checked': {
                               color: '#4CAF50',
                             },
                           }}
                          />
                        }
                        label="Include Address Information"
                        sx={{ color: '#666' }}
                      />
                    </Grid>

                    {/* Security */}
                    <Grid item xs={12}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, mt: 2 }}>
                        <Avatar sx={{ bgcolor: '#4caf50', mr: 2 }}>
                          <Security />
                        </Avatar>
                        <Typography variant="h6" sx={{ color: '#4caf50', fontWeight: 600 }}>
                          Security
                        </Typography>
                      </Box>
                    </Grid>

                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Password"
                        name="password"
                        type={showPassword ? 'text' : 'password'}
                        value={formData.password}
                        onChange={handleChange}
                        variant="outlined"
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Lock sx={{ color: '#4caf50' }} />
                            </InputAdornment>
                          ),
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                onClick={() => setShowPassword(!showPassword)}
                                edge="end"
                                sx={{ color: '#4caf50' }}
                              >
                                {showPassword ? <VisibilityOff /> : <Visibility />}
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            '& fieldset': {
                              borderColor: '#e0e0e0',
                            },
                            '&:hover fieldset': {
                              borderColor: '#4caf50',
                            },
                            '&.Mui-focused fieldset': {
                              borderColor: '#4caf50',
                            },
                          },
                          '& .MuiInputLabel-root': {
                            color: '#666',
                            '&.Mui-focused': {
                              color: '#4caf50',
                            },
                          },
                          '& .MuiInputBase-input': {
                            color: '#333',
                          },
                        }}
                      />
                      {formData.password && (
                        <Box sx={{ mt: 2, mb: 2 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                            <Typography variant="body2" sx={{ color: getPasswordStrengthColor() }}>
                              {getPasswordStrengthText()}
                            </Typography>
                            <Box sx={{ 
                              display: 'flex', 
                              gap: 0.5,
                              flex: 1,
                              maxWidth: 100
                            }}>
                              {[...Array(5)].map((_, index) => (
                                <Box
                                  key={index}
                                  sx={{
                                    height: 4,
                                    flex: 1,
                                    backgroundColor: index < passwordStrength.score ? getPasswordStrengthColor() : '#e0e0e0',
                                    borderRadius: 1
                                  }}
                                />
                              ))}
                            </Box>
                          </Box>
                          {passwordStrength.feedback && (
                            <Typography variant="caption" sx={{ color: '#666', fontSize: '0.75rem' }}>
                              Requirements: {passwordStrength.feedback}
                            </Typography>
                          )}
                        </Box>
                      )}
                    </Grid>

                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Confirm Password"
                        name="confirmPassword"
                        type={showConfirmPassword ? 'text' : 'password'}
                        value={formData.confirmPassword}
                        onChange={handleChange}
                        variant="outlined"
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Lock sx={{ color: '#4caf50' }} />
                            </InputAdornment>
                          ),
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                edge="end"
                                sx={{ color: '#4caf50' }}
                              >
                                {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            '& fieldset': {
                              borderColor: '#e0e0e0',
                            },
                            '&:hover fieldset': {
                              borderColor: '#4caf50',
                            },
                            '&.Mui-focused fieldset': {
                              borderColor: '#4caf50',
                            },
                          },
                          '& .MuiInputLabel-root': {
                            color: '#666',
                            '&.Mui-focused': {
                              color: '#4caf50',
                            },
                          },
                          '& .MuiInputBase-input': {
                            color: '#333',
                          },
                        }}
                      />
                    </Grid>
                  </Grid>

                  <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    size="large"
                    disabled={loading}
                                         sx={{
                       mt: 4,
                       mb: 3,
                       py: 2,
                       background: 'linear-gradient(135deg, #2E7D32 0%, #4CAF50 100%)',
                       color: 'white',
                       fontWeight: 'bold',
                       fontSize: '1.1rem',
                       borderRadius: 3,
                       '&:hover': {
                         background: 'linear-gradient(135deg, #1B5E20 0%, #388E3C 100%)',
                         transform: 'translateY(-2px)',
                         boxShadow: '0 8px 25px rgba(46, 125, 50, 0.3)',
                       },
                       '&:disabled': {
                         background: 'linear-gradient(135deg, #a5d6a7 0%, #c8e6c9 100%)',
                         color: 'rgba(255, 255, 255, 0.7)',
                       },
                       transition: 'all 0.3s ease',
                     }}
                  >
                    {loading ? (
                      <CircularProgress size={28} sx={{ color: 'white' }} />
                    ) : (
                      'Create Account'
                    )}
                  </Button>

                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="body1" sx={{ color: '#666' }}>
                      Already have an account?{' '}
                                             <Link 
                         to="/login" 
                         style={{ 
                           color: '#2E7D32', 
                           textDecoration: 'none',
                           fontWeight: 'bold'
                         }}
                       >
                        Sign in here
                      </Link>
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default Register;
